import moment from 'moment';

import * as React from 'react';
import { mergeClasses } from '../constants';
import Calendar, { Day, Week } from './Calendar';

export const INITIAL_CALENDAR_STATE = {
  start: null,
  end: null,
  hovering: null,
};

export type CalendarState = {
  start: moment.Moment | null;
  end: moment.Moment | null;
  hovering: moment.Moment | null;
};

type Brush = (
  arg0: CalendarState & {
    day: moment.Moment;
    maxRangeDays: number;
  },
) => string | null;

const Brushes: {
  [key: string]: Brush;
} = {
  startNoEnd: (state) => {
    if (!state.start || !state.day.isSame(state.start, 'day')) {
      return null;
    } else if (state.hovering && state.hovering.isAfter(state.start, 'day')) {
      return 'start after';
    } else if (state.hovering && state.hovering.isBefore(state.start, 'day')) {
      return 'start before';
    } else {
      return 'start';
    }
  },
  startWithEnd: (state) => {
    if (!state.start || !state.day.isSame(state.start, 'day')) {
      return null;
    } else if (state.end && state.end.isAfter(state.start, 'day')) {
      return 'start after';
    } else {
      return 'start';
    }
  },

  end: (state) => {
    const { day, start, end } = state;
    if (start && end) {
      if (day.isSame(end, 'day') && !day.isSame(start, 'day')) {
        return 'end';
      }
    }
    return null;
  },
  hover: (state) =>
    state.hovering && state.day.isSame(state.hovering, 'day') ? 'target' : null,

  selected: (state) => {
    const { day, start, end } = state;
    if (start && end) {
      if (day.isBetween(start, end, 'day')) {
        return 'sel';
      }
    }
    return null;
  },

  selectStartToHover: (state) => {
    const { day, start, hovering } = state;
    if (hovering && start) {
      if (
        day.isBetween(start, hovering, 'day') ||
        day.isBetween(hovering, start, 'day')
      ) {
        return 'sel';
      }
    }
    return null;
  },

  outOfRange: (state) => {
    const { day, start, end } = state;
    if (start && !end) {
      if (Math.abs(start.diff(day, 'days')) > state.maxRangeDays) {
        return 'out-of-range';
      }
    }
    return null;
  },
};

type DayMap = {
  [key: string]: moment.Moment;
};

type Props = {
  title: React.ReactNode;
  className?: string;
  earliestAllowed: moment.Moment;
  latestAllowed: moment.Moment;
  currentState: CalendarState;
  onChange: (arg0: CalendarState) => void;
  maxRangeDays: number;
};

type State = {
  dayMap: DayMap;
  weeks: Array<Week>;
};

const didChange = (
  keys: Array<string>,
  prev: {
    [key: string]: any;
  },
  cur: {
    [key: string]: any;
  },
) => {
  return keys.reduce(
    (result, key) =>
      result || (prev[key] || '').toString() !== (cur[key] || '').toString(),
    false,
  );
};

export default class SelectableRangeCalendar extends React.PureComponent<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = this.buildCalendar(props);
  }

  componentDidUpdate(prevProps: Props) {
    if (
      didChange(['earliestAllowed', 'latestAllowed'], prevProps, this.props)
    ) {
      // rebuild and style calendar
      this.setState(this.buildCalendar(this.props));
    } else {
      if (
        didChange(
          ['start', 'end', 'hovering'],
          prevProps.currentState,
          this.props.currentState,
        )
      ) {
        // only need to restyle
        this.setState((prevState) => ({
          weeks: this.styleWeeks(
            this.props.currentState,
            prevState.weeks,
            this.props.maxRangeDays,
          ),
        }));
      }
    }
  }

  buildCalendar = (props: Props): State => {
    const { earliestAllowed, latestAllowed, currentState, maxRangeDays } =
      props;
    const startOfCal = earliestAllowed.clone().startOf('week'),
      endOfCal = latestAllowed.clone().endOf('week');
    const current = startOfCal.clone();
    let dayMap: DayMap = {},
      weeks: Array<Week> = [],
      week: Week = [];

    while (current.isSameOrBefore(endOfCal)) {
      if (current.isBefore(earliestAllowed) || current.isAfter(latestAllowed)) {
        week.push({
          moment: null,
          className: 'disabled',
        });
      } else {
        const dayMoment = current.clone();
        week.push({
          moment: dayMoment,
          className: null,
        });

        dayMap[current.format('YYYYMMDD')] = dayMoment;
      }
      current.add(1, 'day');
      if (current.day() === 0) {
        weeks.push(week);
        week = [];
      }
    }
    return {
      dayMap,
      weeks: this.styleWeeks(currentState, weeks, maxRangeDays),
    };
  };

  styleWeeks = (
    currentState: CalendarState,
    weeks: Array<Week>,
    maxRangeDays: number,
  ): Array<Week> => {
    // restyle calendar
    const { start, end, hovering } = currentState;
    let brushes: Array<Brush> = [];
    if (hovering) {
      if (start) {
        if (end) {
          // start, end, hovering - show selected range & hover reticle
          brushes = [
            Brushes.startWithEnd,
            Brushes.end,
            Brushes.hover,
            Brushes.selected,
          ];
        } else {
          // start, !end, hovering - show start and highlight to hover, disable out of range
          brushes = [
            Brushes.startNoEnd,
            Brushes.hover,
            Brushes.selectStartToHover,
            Brushes.outOfRange,
          ];
        }
      } else {
        // no start/end, just a hovering selector
        brushes = [Brushes.hover];
      }
    } else {
      // not hovering
      if (start) {
        if (end) {
          // start, end, !hovering - show selected range
          brushes = [Brushes.startWithEnd, Brushes.end, Brushes.selected];
        } else {
          // start, !end, !hovering - only highlight start, disable out of range
          brushes = [Brushes.startNoEnd, Brushes.outOfRange];
        }
      } // else {  !start, (implied !end), !hovering - blank
    }
    return weeks.map((week) =>
      week.map((day: Day) => {
        const dayMoment = day.moment;
        if (dayMoment) {
          return {
            ...day,
            className: mergeClasses(
              brushes.map((brush) =>
                brush({
                  day: dayMoment,
                  start,
                  end,
                  hovering,
                  maxRangeDays,
                }),
              ),
            ),
          };
        }
        return day;
      }),
    );
  };

  onMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    const { onChange, currentState } = this.props;
    const date = event.currentTarget.dataset.date;
    if (date) {
      onChange({
        ...currentState,
        hovering: this.state.dayMap[date],
      });
    }
  };

  onMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const { onChange, currentState } = this.props;
    onChange({ ...currentState, hovering: null });
  };

  onClick = (event: React.MouseEvent<HTMLElement>) => {
    const date = event.currentTarget.dataset.date;
    if (date) {
      const selected = this.state.dayMap[date];
      const { onChange, currentState, maxRangeDays } = this.props;
      const { start, end, hovering } = currentState;

      if (!start || end) {
        // selecting start value
        onChange({ start: selected, end: null, hovering });
      } else if (Math.abs(selected.diff(start, 'days')) <= maxRangeDays) {
        // selecting end
        if (start.isAfter(selected)) {
          // flip them
          onChange({ start: selected, end: start, hovering });
        } else {
          onChange({ start, end: selected, hovering });
        }
      }
    }
  };

  render() {
    const { title, className } = this.props;
    return (
      <Calendar
        className={className}
        title={title}
        weeks={this.state.weeks}
        onDayClick={this.onClick}
        onDayMouseLeave={this.onMouseLeave}
        onDayMouseEnter={this.onMouseEnter}
      />
    );
  }
}
